import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, delay, expand, filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { AppContext } from './app-context';
import { Net } from './net';

/**
 * Sharing file mode.
 */
export enum DeploymentShareMode {
    /**
     * Admin share.
     */
    Admin = 'adminshare',

    /**
     * User share.
     */
    User = 'usershare'
}

/**
 * Download states.
 */
export enum DownloadState {
    /**
     * Download not started.
     */
    NotStarted = 'NotStarted',

    /**
     * Download is running.
     */
    Running = 'Running',

    /**
     * Download is cancelled.
     */
    Cancelled = 'Cancelled',

    /**
     * Download failed.
     */
    Failed = 'Failed',

    /**
     * Download completed.
     */
    Completed = 'Completed'
}

/**
 * Result data of created deployment share.
 */
export interface DeploymentShareResult {
    /**
     * deployment share mode.
     */
    mode: DeploymentShareMode;

    /**
     * Share name.
     */
    name: string;

    /**
     * Created time (ISO time).
     */
    createdTime: string;
}

/**
 * Request data of download.
 */
export interface DownloadRequest {
    /**
     * URL to download.
     */
    url: string;

    /**
     * Path to file to store.
     */
    path: string;

    /**
     * Flag indicating if the file URL is trusted and signature validation can be skipped.
     */
    trustedUrlOmitValidation?: boolean;
}

/**
 * Result data of download.
 */
export interface DownloadResult {
    /**
     * Download identity.
     */
    id: string;

    /**
     * Requested number of items.
     */
    requested: number;

    /**
     * Remaining number of items.
     */
    remaining: number;

    /**
     * Started time (ISO).
     */
    startedTime: string;
}

/**
 * Status data of download.
 */
export interface DownloadStatus {
    /**
     * Download identity.
     */
    id: string;

    /**
     * Requested number of items.
     */
    requested: number;

    /**
     * Remaining number of items.
     */
    remaining: number;

    /**
     * Error number of items.
     */
    errors: number;

    /**
     * Started time (ISO).
     */
    startedTime: string;

    /**
     * Completed time (ISO).
     */
    completedTime: string;

    /**
     * Last error message.
     */
    lastError?: string;

    /**
     * Sharing mode.
     */
    mode: DeploymentShareMode;

    /**
     * Name of network share.
     */
    name: string;

    /**
     * State of the download.
     */
    downloadState: DownloadState;
}

/**
 * Status of file on the shared folder.
 */
export interface FileStatus {
    /**
     * Url of download source.
     */
    url: string;

    /**
     * Timestamp when download completed (ISO).
     */
    timestamp: string;

    /**
     * File path within the share folder.
     */
    path: string;
}

/**
 * Secure deployment share class.
 */
export class DeploymentShare {
    private static deploymentShareUrl = 'deployment/{0}';
    private static deploymentShareNameUrl = 'deployment/{0}/{1}';
    private static deploymentShareDownloadUrl = 'deployment/{0}/{1}/download';
    private static deploymentShareDownloadIdUrl = 'deployment/{0}/{1}/download/{2}';
    private static deploymentShareDownloadCancelUrl = 'deployment/{0}/{1}/download/{2}/cancel';
    private static deploymentShareStatusUrl = 'deployment/status';
    private static deploymentShareFileUrl = 'deployment/{0}/{1}/file';
    private static deploymentShareFilePathUrl = 'deployment/{0}/{1}/file/{2}';
    private static deploymentCopyUrl = 'deployment/{0}/{1}/copy';

    /**
     * Initializes a new instance of the DeploymentShare class.
     *
     * @param appContext the application context.
     */
    constructor(private appContext: AppContext) { }

    /**
     * Create a new deployment network share. (elevate the desktop gateway if required.)
     *
     * @param shareMode the sharing mode.
     * @param shareName the name of share.
     */
    public createShare(shareMode: DeploymentShareMode, shareName: string): Observable<DeploymentShareResult> {
        const url = DeploymentShare.deploymentShareNameUrl.format(shareMode, shareName);
        return this.appContext.gateway.put(url, '{}')
            .pipe(
                catchError((error, caught) => {
                    // catch elevation error and retry after elevated.
                    if (this.appContext.gateway.isElevationRequired(error)) {
                        return this.appContext.gateway.pollingGatewayElevated()
                            .pipe(switchMap(elevated => elevated ? caught : throwError(() => error)));
                    }

                    return throwError(() => error);
                }),
                map((response: DeploymentShareResult) => {
                    if (!response) {
                        throw new Error('DeploymentShare createShare() call is failed with null response result.');
                    }

                    return response;
                })
            );
    }

    /**
     * Delete the deployment network share. (elevate the desktop gateway if required.)
     *
     * @param shareMode the sharing mode.
     * @param shareName the name of share.
     */
    public deleteShare(shareMode: DeploymentShareMode, shareName: string): Observable<void> {
        const url = DeploymentShare.deploymentShareNameUrl.format(shareMode, shareName);
        return this.appContext.gateway.delete(url)
            .pipe(
                catchError((error, caught) => {
                    // catch elevation error and retry after elevated.
                    if (this.appContext.gateway.isElevationRequired(error)) {
                        return this.appContext.gateway.pollingGatewayElevated()
                            .pipe(switchMap(elevated => elevated ? caught : throwError(() => error)));
                    }

                    return throwError(() => error);
                }),
                map(() => null)
            );
    }

    /**
     * List existing deployment network shares.
     *
     * @param shareMode the sharing mode.
     */
    public queryShares(shareMode: DeploymentShareMode): Observable<DeploymentShareResult[]> {
        const url = DeploymentShare.deploymentShareUrl.format(shareMode);
        return this.appContext.gateway.get(url)
            .pipe(map((response: any) => {
                const value: DeploymentShareResult[] = response && response.value;
                if (!value) {
                    throw new Error('DeploymentShare queryShares() call is failed with null response result.');
                }

                return value;
            }));
    }

    /**
     * Download files from specified urls and store to the deployment network share folder.
     *
     * @param shareMode the sharing mode.
     * @param shareName the name of share.
     * @param request the request object.
     * @param timeout the timeout for the download in milliseconds.
     * @param cancelReady (Optional) the cancel ready callback. Retain the supplied ID to call cancelDownload().
     * @param polling (Optional) the polling timer milliseconds (default is 2000, and minimum is 1000).
     */
    public downloadFiles(
        shareMode: DeploymentShareMode,
        shareName: string,
        request: DownloadRequest[],
        timeout?: number,
        cancelReady?: (id: string) => void,
        polling?: number): Observable<DownloadStatus> {

        // default polling every 2 seconds, and minimum every 1 second.
        polling = polling == null ? 2000 : Math.max(polling, 1000);
        const url = DeploymentShare.deploymentShareDownloadUrl.format(shareMode, shareName);
        const body = JSON.stringify({ value: request, timeout: timeout });
        return this.appContext.gateway.post(url, body)
            .pipe(
                mergeMap((response: DownloadResult) => {
                    if (!response) {
                        return throwError(() => new Error('DeploymentShare downloadFiles() call is failed with null response or empty Location header.'));
                    }

                    if (cancelReady) {
                        cancelReady(response.id);
                    }

                    const statusUrl = DeploymentShare.deploymentShareDownloadIdUrl.format(shareMode, shareName, response.id);
                    return this.queryStatus(statusUrl)
                        .pipe(
                            expand((status) => {
                                if (status.downloadState === DownloadState.Running) {
                                    return of(null)
                                        .pipe(
                                            delay(polling),
                                            mergeMap(() => this.queryStatus(statusUrl))
                                        );
                                }

                                return EMPTY;
                            }),
                            filter(status =>
                                status.downloadState !== DownloadState.Running &&
                                status.downloadState !== DownloadState.NotStarted
                            ));
                })
            );
    }

    /**
     * Cancel the download by ID.
     *
     * @param shareMode the sharing mode.
     * @param shareName the name of share.
     * @param id the identification of download call responded by cancelReady() call back.
     */
    public cancelDownload(shareMode: DeploymentShareMode, shareName: string, id: string): Observable<void> {
        const url = DeploymentShare.deploymentShareDownloadCancelUrl.format(shareMode, shareName, id);
        return this.appContext.gateway.post(url, '{}')
            .pipe(map(() => null));
    }

    /**
     * Query active downloads with filtering conditions.
     *
     * @param shareMode the sharing mode.
     * @param shareName the name of share.
     */
    public queryDownloads(shareMode?: DeploymentShareMode, shareName?: string): Observable<DownloadStatus[]> {
        const url = DeploymentShare.deploymentShareStatusUrl;
        return this.appContext.gateway.get(url)
            .pipe(
                map((response: DownloadStatus[]) => {
                    if (!response) {
                        throw new Error('DeploymentShare queryDownloads() call is failed with null response.');
                    }

                    return response.filter(item => (!shareMode || item.mode === shareMode) && (!shareName || item.name === shareName));
                })
            );
    }

    /**
     * Query files on the share.
     *
     * @param shareMode the sharing mode.
     * @param shareName the name of share.
     */
    public queryFiles(shareMode: DeploymentShareMode, shareName: string): Observable<FileStatus[]> {
        const url = DeploymentShare.deploymentShareFileUrl.format(shareMode, shareName);
        return this.appContext.gateway.get(url)
            .pipe(
                map((response: any) => {
                    const files: FileStatus[] = response && response.files;
                    if (!files) {
                        throw new Error('DeploymentShare queryFiles() call is failed with null response.');
                    }

                    return files;
                })
            );
    }

    /**
     * Delete a file or a directory on the share.
     *
     * @param shareMode the sharing mode.
     * @param shareName the name of share.
     * @param path the path to the file or the directory on the share.
     */
    public deleteItem(shareMode: DeploymentShareMode, shareName: string, path: string): Observable<void> {
        const path64 = Net.base64urlEncode(path);
        const url = DeploymentShare.deploymentShareFilePathUrl.format(shareMode, shareName, path64);
        return this.appContext.gateway.delete(url)
            .pipe(map(() => null));
    }

    /**
     * Create a text/XML file in network share.
     *
     * @param shareMode the sharing mode.
     * @param shareName the name of share.
     * @param path the path to the file or the directory on the share.
     * @param content the linese of content for the file to create.
     */
    public createTextFile(shareMode: DeploymentShareMode, shareName: string, path: string, content: string[]): Observable<void> {
        const path64 = Net.base64urlEncode(path);
        const url = DeploymentShare.deploymentShareFilePathUrl.format(shareMode, shareName, path64);

        const body = JSON.stringify({ content: content });

        return this.appContext.gateway.post(url, body).pipe(map(() => null));
    }

    /**
     * Copy file or folder in network share.
     * @param shareMode The share mode.
     * @param shareName the name of the share.
     * @param source The source path of the item to copy relative to the network share.
     * @param destination The destination path of the item to copy relative to the network share.
     */
    public copyItem(shareMode: DeploymentShareMode, shareName: string, source: string, destination: string): Observable<void> {
        const url = DeploymentShare.deploymentCopyUrl.format(shareMode, shareName);

        const body = JSON.stringify({ source: source, destination: destination });

        return this.appContext.gateway.post(url, body).pipe(map(() => null));
    }

    private queryStatus(statusUrl: string): Observable<DownloadStatus> {
        return this.appContext.gateway.get(statusUrl)
            .pipe(map((response: DownloadStatus) => {
                if (!response) {
                    throw new Error('DeploymentShare download() call is failed with null response for status.');
                }

                return response;
            }));
    }
}
